import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  validateIronPythonScriptGrpc,
  type IronPythonRuntimeValidationResult,
} from '../../../shared/api/grpc/pcfMappingClient';
import {
  analyzeIronPython,
  type IronPythonDiagnostic,
} from '../lib/ironPythonSyntax';

export type IronPythonRuntimeStatus = 'checking' | 'valid' | 'invalid' | 'unavailable';

export interface IronPythonSaveValidation {
  canSave: boolean;
  source: string;
  diagnostics: IronPythonDiagnostic[];
}

export interface IronPythonValidationModel {
  diagnostics: IronPythonDiagnostic[];
  errorCount: number;
  warningCount: number;
  runtimeStatus: IronPythonRuntimeStatus;
  runtimeError?: string;
  validateForSave: () => Promise<IronPythonSaveValidation>;
}

interface RuntimeSnapshot {
  source: string;
  status: IronPythonRuntimeStatus;
  diagnostics: IronPythonDiagnostic[];
  error?: string;
}

const RuntimeDebounceMs = 500;
const RuntimeTimeoutMs = 5_000;

const toRuntimeSnapshot = (
  source: string,
  result: IronPythonRuntimeValidationResult
): RuntimeSnapshot => {
  if (!result.available || !result.success) {
    return {
      source,
      status: 'unavailable',
      diagnostics: [],
      error: result.error || 'IronPython validation is unavailable.',
    };
  }

  const diagnostics: IronPythonDiagnostic[] = result.diagnostics.map((diagnostic) => ({
    ...diagnostic,
    source: 'ironpython',
  }));
  return {
    source,
    status: diagnostics.some(({ severity }) => severity === 'error') ? 'invalid' : 'valid',
    diagnostics,
  };
};

export const useIronPythonValidation = (source: string): IronPythonValidationModel => {
  const localAnalysis = useMemo(() => analyzeIronPython(source), [source]);
  const sourceRef = useRef(source);
  const requestSequenceRef = useRef(0);
  const activeControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({
    source,
    status: 'checking',
    diagnostics: [],
  });
  sourceRef.current = source;

  const runRuntimeValidation = useCallback(async (sourceToValidate: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    activeControllerRef.current?.abort();
    const controller = new AbortController();
    activeControllerRef.current = controller;
    const sequence = ++requestSequenceRef.current;
    setRuntime({ source: sourceToValidate, status: 'checking', diagnostics: [] });
    const timeout = setTimeout(() => controller.abort(), RuntimeTimeoutMs);

    let snapshot: RuntimeSnapshot;
    try {
      const result = await validateIronPythonScriptGrpc(sourceToValidate, controller.signal);
      snapshot = toRuntimeSnapshot(sourceToValidate, result);
    } catch {
      snapshot = {
        source: sourceToValidate,
        status: 'unavailable',
        diagnostics: [],
        error: 'IronPython validation timed out or was cancelled.',
      };
    } finally {
      clearTimeout(timeout);
    }

    if (sequence === requestSequenceRef.current && sourceRef.current === sourceToValidate) {
      setRuntime(snapshot);
    }
    return snapshot;
  }, []);

  useEffect(() => {
    activeControllerRef.current?.abort();
    const sequence = ++requestSequenceRef.current;
    setRuntime({ source, status: 'checking', diagnostics: [] });
    debounceTimerRef.current = setTimeout(async () => {
      debounceTimerRef.current = null;
      if (sequence !== requestSequenceRef.current) {
        return;
      }
      await runRuntimeValidation(source);
    }, RuntimeDebounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      activeControllerRef.current?.abort();
    };
  }, [runRuntimeValidation, source]);

  const validateForSave = useCallback(async (): Promise<IronPythonSaveValidation> => {
    let sourceToValidate = sourceRef.current;
    while (true) {
      const local = analyzeIronPython(sourceToValidate);
      if (local.errorCount > 0) {
        return { canSave: false, source: sourceToValidate, diagnostics: local.diagnostics };
      }

      const exact = await runRuntimeValidation(sourceToValidate);
      if (sourceRef.current !== sourceToValidate) {
        sourceToValidate = sourceRef.current;
        continue;
      }

      return {
        canSave: !exact.diagnostics.some(({ severity }) => severity === 'error'),
        source: sourceToValidate,
        diagnostics: exact.diagnostics,
      };
    }
  }, [runRuntimeValidation]);

  const currentRuntime = runtime.source === source
    ? runtime
    : { source, status: 'checking' as const, diagnostics: [] };
  const diagnostics = useMemo(() => [
    ...localAnalysis.diagnostics.map((diagnostic) => ({ ...diagnostic, source: 'local' as const })),
    ...currentRuntime.diagnostics,
  ], [currentRuntime.diagnostics, localAnalysis.diagnostics]);

  return {
    diagnostics,
    errorCount: diagnostics.filter(({ severity }) => severity === 'error').length,
    warningCount: diagnostics.filter(({ severity }) => severity === 'warning').length,
    runtimeStatus: currentRuntime.status,
    runtimeError: currentRuntime.error,
    validateForSave,
  };
};
